<script lang="ts">
    import { getMedia, getTags } from "../api"
    import Breadcrumbs from "./Breadcrumbs.svelte"
    import { sprache } from "../store"
    import { debounce } from "lodash"
    import Lightbox from "./Lightbox.svelte"

    let projectSections: Tag[] = []
    let topicTags: Tag[] = []
    let subtopicTags: Tag[] = []
    let galleryTags: Tag[] = []
    let mediaTags: Media[] = []

    async function loadMediaTags() {
        projectSections = await getTags("projectSection")
        topicTags = await getTags("topic")
        subtopicTags = await getTags("subtopic")
        galleryTags = await getTags("gallery")

        mediaTags = await getMedia(
            {
                tags: { $in: projectSections.map((tag) => tag.id) },
            },
            "-date",
            "tags"
        )
    }
    loadMediaTags()

    // check if a media exists for all given tags
    function checkTags(tags: string[]) {
        return mediaTags.some((media) => {
            return tags.every((tag) => media.tags.includes(tag))
        })
    }

    let projectSectionTagId = ""

    // reduce topic tags to only those that are in mediaTags and have the projectSectionTagId
    $: _topicTags = topicTags.filter((tag) => checkTags([projectSectionTagId, tag.id]))
    $: if (!_topicTags.length) topicTagId = ""

    let topicTagId = ""

    // reduce subtopic tags to only those that are in mediaTags and have the projectSectionTagId and topicTagId
    $: _subtopicTags = subtopicTags.filter((tag) => checkTags([projectSectionTagId, topicTagId, tag.id]))
    $: if (!_subtopicTags.length) subtopicTagId = ""

    let subtopicTagId = ""

    let search = ""

    let media: Media[] = []

    let filterParam = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("filter")

    let mediaTypeFilter = filterParam ? filterParam : "all"
    /* let mediaTypeFilter = "all" */

    let limitView = 9
    let yearFilter: number
    let loadingMedia = false
    async function loadMedia() {
        loadingMedia = true

        // reset view
        limitView = 9
        yearFilter = null

        let tags = [projectSectionTagId, topicTagId, subtopicTagId].filter((tag) => tag)
        const filter = {}

        if (!tags.length && !search && !projectSections?.length) {
            media = []
            loadingMedia = false
            return
        }

        if (tags.length) {
            filter["tags"] = { $all: tags }
        } else if (projectSections?.length) {
            filter["tags"] = { $in: projectSections.map((tag) => tag.id) }
        }
        if (search) {
            filter["$text"] = { $search: search }
        }

        switch (mediaTypeFilter) {
            case "photo":
                filter["$and"] = [
                    { "file.type": { $in: ["image/jpeg", "image/png"] } },
                    { "external.type": { $not: { $regex: "[Yy]ou[Tt]ube" } } },
                ]

                break
            case "video":
                filter["$or"] = [
                    { "file.type": { $in: ["video/mp4", "video/quicktime", "video/webm"] } },
                    { "external.type": { $regex: "[Yy]ou[Tt]ube" } },
                    { "external.url": { $regex: "[Yy]ou[Tt]ube" } },
                ]

                break
            case "document":
                filter["file.type"] = { $in: ["application/pdf"] }
                break
            case "map":
                filter["external.type"] = "Infografik"
                break
        }

        const _media = await getMedia(filter, "-date", null)
        media = _media
        loadingMedia = false
    }

    const loadMediaDebounced = debounce(loadMedia, 500)

    $: if (projectSections?.length || projectSectionTagId || topicTagId || subtopicTagId || search) {
        if (mediaTypeFilter) loadMediaDebounced()
    } else {
        media = []
    }

    let years: number[] = []

    $: years = media
        .map((media) => new Date(media.date).getFullYear())
        .filter((year, index, self) => self.indexOf(year) === index)

    $: mediaYearFiltered = media.filter((media) => {
        if (!yearFilter) return true
        return new Date(media.date).getFullYear() === yearFilter
    })

    // reset limit when yearFilter changes
    $: if (yearFilter) limitView = 9

    if (typeof window !== "undefined") {
        window.addEventListener(
            "scroll",
            () => {
                const { scrollTop, scrollHeight, clientHeight } = document.documentElement

                if (scrollTop + clientHeight >= scrollHeight - 5 && mediaYearFiltered.length > limitView) {
                    limitView += 9
                }
            },
            {
                passive: true,
            }
        )
    }

    const handleDownload = (e) => {
        if (
            confirm(
                "Alle Inhalte des Internetauftritts VDE 8 sind urheberrechtlich geschützt und dürfen nur mit schriftlicher Genehmigung der Deutschen Bahn AG ganz oder teilweise vervielfältigt, bearbeitet, verbreitet, öffentlich wiedergegeben oder in sonstiger Form genutzt werden."
            )
        ) {
        } else {
            e.preventDefault()
        }
    }
</script>

<section class="mediathek">
    <div class="wrapper">
        <h3>Mediathek</h3>
        <div class="grid mediathek">
            <div class="grid-item">
                <!-- svelte-ignore a11y-invalid-attribute -->
                <a
                    href="#"
                    class:active="{mediaTypeFilter === 'all'}"
                    on:click|preventDefault="{() => {
                        mediaTypeFilter = 'all'
                    }}"
                    ><img src="assets/icons/ic-db-alle-01.svg" alt="alle" />
                    Alle</a
                >
            </div>
            <div class="grid-item">
                <!-- svelte-ignore a11y-invalid-attribute -->
                <a
                    href="#"
                    class:active="{mediaTypeFilter === 'photo'}"
                    on:click|preventDefault="{() => {
                        mediaTypeFilter = 'photo'
                    }}"><img src="assets/icons/ic-db-camera-01.svg" alt="Fotos" />Fotos</a
                >
            </div>
            <div class="grid-item">
                <!-- svelte-ignore a11y-invalid-attribute -->
                <a
                    href="#"
                    class:active="{mediaTypeFilter === 'video'}"
                    on:click|preventDefault="{() => {
                        mediaTypeFilter = 'video'
                    }}"><img src="assets/icons/ic-db-il_pulse_movie.svg" alt="Videos" />Videos</a
                >
            </div>
            <div class="grid-item">
                <!-- svelte-ignore a11y-invalid-attribute -->
                <a
                    href="#"
                    class:active="{mediaTypeFilter === 'document'}"
                    on:click|preventDefault="{() => {
                        mediaTypeFilter = 'document'
                    }}"><img src="assets/icons/ic-db-dokumente-01.svg" alt="Dokumente" />Dokumente</a
                >
            </div>
            <div class="grid-item">
                <!-- svelte-ignore a11y-invalid-attribute -->
                <a
                    href="#"
                    class:active="{mediaTypeFilter === 'map'}"
                    on:click|preventDefault="{() => {
                        mediaTypeFilter = 'map'
                    }}"><img src="assets/icons/ic-db-il_pulse_itinerary.svg" alt="" />Karten</a
                >
            </div>
        </div>
        <div class="row filter-row">
            <div class="filter">
                <label for="projektabschnitt">Projektabschnitt</label>
                <select name="projektabschnitt" id="projektabschnitt" bind:value="{projectSectionTagId}">
                    <option value="" selected>Alle</option>
                    {#each projectSections as tag}
                        <option value="{tag.id}">{tag.name && tag.name[$sprache]}</option>
                    {/each}
                </select>
            </div>
            {#if _topicTags.length}
                <div class="filter">
                    <label for="thema">Thema</label>
                    <select name="thema" id="thema" bind:value="{topicTagId}">
                        <option value="" selected>Alle</option>
                        {#each _topicTags as tag}
                            <option value="{tag.id}">{tag.name && tag.name[$sprache]}</option>
                        {/each}
                    </select>
                </div>
            {/if}
            {#if _subtopicTags.length}
                <div class="filter">
                    <label for="unterthema">Unterthema</label>
                    <select name="unterthema" id="unterthema" bind:value="{subtopicTagId}">
                        <option value="" selected>Alle</option>
                        {#each _subtopicTags as tag}
                            <option value="{tag.id}">{tag.name && tag.name[$sprache]}</option>
                        {/each}
                    </select>
                </div>
            {/if}
            <div class="filter">
                <label for="mediatheksuche">Mediatheksuche</label>
                <input type="text" id="mediatheksuche" class="search" bind:value="{search}" />
            </div>
        </div>
    </div>
</section>
<section class="mediathek-result border-top">
    <div class="wrapper">
        <div class="row filter-years">
            <ul>
                {#each years.filter((y) => y != 1970) as year}
                    <li>
                        <!-- svelte-ignore a11y-invalid-attribute -->
                        <a
                            href="#"
                            class:active="{yearFilter === year}"
                            on:click|preventDefault="{() => {
                                if (yearFilter === year) yearFilter = null
                                else yearFilter = year
                            }}">{year}</a
                        >
                    </li>
                {/each}
            </ul>
        </div>
        <div class="hinweis">
            Alle Inhalte des Internetauftritts VDE 8 sind urheberrechtlich geschützt und dürfen nur mit schriftlicher
            Genehmigung der Deutschen Bahn AG ganz oder teilweise vervielfältigt, bearbeitet, verbreitet, öffentlich
            wiedergegeben oder in sonstiger Form genutzt werden.
        </div>
        {#if loadingMedia}
            lade...
        {:else}
            <div class="grid mediathek">
                {#each mediaYearFiltered.slice(0, limitView) as m (m.id)}
                    <div
                        class="grid-item"
                        data-type="{m.file?.type} {m.external?.type != 'Keine' && m.external?.type != undefined
                            ? m.external?.type
                            : ''}"
                    >
                        <Lightbox m="{m}" />

                        <div class="item-content">
                            <h4>
                                {@html (m.title && m.title[$sprache]) ||
                                    "<span class='filename'>" + m.filename + "</span>"}
                            </h4>
                            <p>{(m.author && m.author[$sprache]) || ""}</p>
                            {#if m.file?.type == "image/jpeg" || m.file?.type == "image/png"}
                                <!-- svelte-ignore a11y-missing-content -->
                                <a
                                    href="/api/media/{m.id}/{m.file?.src || m.preview?.src}"
                                    target="_blank"
                                    class="icon download"
                                    on:click="{(e) => handleDownload(e)}"
                                    download="{m.filename}"></a>
                            {/if}
                            {#if m.file?.type == "application/pdf"}
                                <!-- svelte-ignore a11y-missing-content -->
                                <a
                                    href="/api/media/{m.id}/{m.file?.src}"
                                    class="icon download"
                                    target="_blank"
                                    on:click="{(e) => handleDownload(e)}"
                                    download="{m.filename}"></a>
                            {/if}
                        </div>
                    </div>
                {/each}
            </div>
        {/if}
    </div>
</section>

<style>
    a.active {
        color: #ec0016;
        font-weight: bold;
    }
</style>
